/**
 * Proposal Engagement — view-tracking analytics that ACT (PURE core).
 *
 * Closes three auditor findings in one module:
 *
 *   • "phantom analytics" — {@link recordEngagement} / {@link summarizeEngagement}
 *     are a real append-only event log + deterministic rollup (opens, unique
 *     recipients, dwell, hottest section, stall verdict), not a mocked chart;
 *   • "chase unwired" — {@link detectChaseSignals} turns the rollup into
 *     concrete {@link ChaseSignal}s and {@link draftChase} writes the actual
 *     follow-up: the REAL Argo agent-worker drafts it when a worker is
 *     injected (agentic, not mail-merge), and {@link fallbackChaseCopy}
 *     supplies a DETERMINISTIC per-reason template when the worker is absent
 *     or throws — the whole path is gate-passable offline;
 *   • "expiry missing" — 'expiring_soon' fires as `validUntil` approaches and
 *     a PAST `validUntil` is terminal: no signal ever chases a dead proposal.
 *
 * Governance: {@link gateChase} enforces approval-gated sends per earned
 * autonomy tier. A send auto-executes ONLY when the signal severity is within
 * both the earned tier and the configured severity ceiling — and cold
 * outreach on an unopened proposal NEVER auto-sends, regardless of tier
 * (policy risk: we will not spam a buyer who has shown zero engagement).
 * Everything else becomes an approvals-inbox item via
 * {@link chaseApprovalAction} (queueApproval → operator sign-off → executor).
 *
 * Deliberately pure: no store, no firebase, no ambient clocks — `now` is
 * always injected. The worker seam is a tiny injected `generate(prompt)`
 * so tests stub it and the UI adapts src/lib/agent/argoWorker.ts to it.
 * This module is NOT src/lib/agent/proposalChase.ts (state-machine moves
 * from coarse telemetry): it owns the event log, the expiry clock, the
 * worker drafting seam, and the tiered gate that module lacked.
 */

import type { AgentAction } from '../agent/types';

// ---------------------------------------------------------------------------
// Event log
// ---------------------------------------------------------------------------

/** One buyer-side engagement event, recorded verbatim — never invented. */
export type EngagementEvent = {
  kind: 'opened' | 'section_view' | 'accepted' | 'declined';
  /** Which proposal section (for section_view), e.g. 'pricing'. */
  sectionId?: string;
  /** Milliseconds the recipient dwelled (for section_view). */
  dwellMs?: number;
  /** Opaque per-recipient token from the tracked link. */
  recipientToken: string;
  /** Epoch ms at capture — caller-supplied, deterministic in tests. */
  ts: number;
};

/** Append-only engagement log for one sent proposal. */
export interface EngagementLog {
  docId: string;
  events: EngagementEvent[];
}

/** Re-opens by the same recipient inside this window are one read (reload). */
export const OPEN_DEDUPE_WINDOW_MS = 5 * 60_000;

/** Opened but unaccepted for this long → the deal is stalling. */
export const STALL_THRESHOLD_MS = 48 * 3_600_000;

/** Never opened this long after send → the proposal went into a void. */
export const UNOPENED_AFTER_MS = 3 * 86_400_000;

/** validUntil inside this horizon → 'expiring_soon'. */
export const EXPIRING_SOON_MS = 72 * 3_600_000;

/**
 * Append one event to the log. Append-only and immutable: the input log is
 * never mutated. An exact-duplicate open — same recipient re-opening within
 * {@link OPEN_DEDUPE_WINDOW_MS} of one of their prior opens — is a page
 * reload, not a second read, and is dropped; a later open (outside the
 * window) is a genuine return visit and is kept.
 */
export function recordEngagement(log: EngagementLog, ev: EngagementEvent): EngagementLog {
  if (ev.kind === 'opened') {
    const isReload = log.events.some(
      (e) =>
        e.kind === 'opened' &&
        e.recipientToken === ev.recipientToken &&
        Math.abs(ev.ts - e.ts) <= OPEN_DEDUPE_WINDOW_MS,
    );
    if (isReload) return log;
  }
  return { ...log, events: [...log.events, ev] };
}

// ---------------------------------------------------------------------------
// Rollup
// ---------------------------------------------------------------------------

export interface EngagementSummary {
  opens: number;
  uniqueRecipients: number;
  totalDwellMs: number;
  /** Section with the greatest cumulative dwell; null when none tracked. */
  hottestSectionId: string | null;
  /** Opened-but-unaccepted past threshold, or unopened N days after send. */
  stalled: boolean;
  lastOpenTs: number | null;
}

/**
 * Roll the log up into the numbers the engagement panel shows and the chase
 * detector consumes. Deterministic: `opts.now` is the injected clock
 * (defaults to the latest event's ts so a pure replay needs no wall clock);
 * `opts.sentAt` lets an unopened proposal be honestly judged stale — without
 * it we cannot claim staleness for an empty log, so we don't.
 */
export function summarizeEngagement(
  log: EngagementLog,
  opts: { now?: number; sentAt?: number } = {},
): EngagementSummary {
  let opens = 0;
  let totalDwellMs = 0;
  let firstOpenTs: number | null = null;
  let lastOpenTs: number | null = null;
  let accepted = false;
  let declined = false;
  const recipients = new Set<string>();
  const dwellBySection = new Map<string, number>();
  let latestTs = Number.NEGATIVE_INFINITY;

  for (const e of log.events) {
    recipients.add(e.recipientToken);
    if (e.ts > latestTs) latestTs = e.ts;
    switch (e.kind) {
      case 'opened':
        opens += 1;
        if (firstOpenTs === null || e.ts < firstOpenTs) firstOpenTs = e.ts;
        if (lastOpenTs === null || e.ts > lastOpenTs) lastOpenTs = e.ts;
        break;
      case 'section_view': {
        const dwell = typeof e.dwellMs === 'number' && e.dwellMs > 0 ? e.dwellMs : 0;
        totalDwellMs += dwell;
        if (e.sectionId) {
          dwellBySection.set(e.sectionId, (dwellBySection.get(e.sectionId) ?? 0) + dwell);
        }
        break;
      }
      case 'accepted':
        accepted = true;
        break;
      case 'declined':
        declined = true;
        break;
    }
  }

  let hottestSectionId: string | null = null;
  let hottestDwell = -1;
  for (const [id, dwell] of dwellBySection) {
    if (dwell > hottestDwell) {
      hottestDwell = dwell;
      hottestSectionId = id;
    }
  }

  const now =
    opts.now ?? (Number.isFinite(latestTs) ? latestTs : Date.now());

  let stalled = false;
  if (!accepted && !declined) {
    if (firstOpenTs !== null) {
      stalled = now - firstOpenTs > STALL_THRESHOLD_MS;
    } else if (typeof opts.sentAt === 'number') {
      stalled = now - opts.sentAt > UNOPENED_AFTER_MS;
    }
  }

  return {
    opens,
    uniqueRecipients: log.events.length === 0 ? 0 : recipients.size,
    totalDwellMs,
    hottestSectionId,
    stalled,
    lastOpenTs,
  };
}

// ---------------------------------------------------------------------------
// Chase signals — fixes the "expiry missing" finding
// ---------------------------------------------------------------------------

export interface ChaseSignal {
  docId: string;
  reason: 'unopened' | 'viewed_no_action' | 'stalled_on_pricing' | 'expiring_soon';
  /** 1 (mild) … 5 (urgent). Compared against the earned autonomy tier. */
  severity: number;
}

/** Severity per reason — expiry outranks stalls outranks cold silence. */
export const SIGNAL_SEVERITY: Record<ChaseSignal['reason'], number> = {
  unopened: 2,
  viewed_no_action: 2,
  stalled_on_pricing: 3,
  expiring_soon: 4,
};

/** Section ids that mean "the buyer is stuck on the money". */
function isPricingSection(sectionId: string): boolean {
  const s = sectionId.toLowerCase();
  return s.includes('pricing') || s.includes('investment') || s.includes('quote');
}

/**
 * Detect the chase-worthy conditions on one proposal:
 *
 *   • past `validUntil` → TERMINAL: [] — an expired proposal is history,
 *     never a chase target (this is the missing-expiry fix);
 *   • accepted/declined in the log → terminal, [];
 *   • no opens ≥ {@link UNOPENED_AFTER_MS} after `sentAt` → 'unopened';
 *   • opened + stalled with pricing the hottest section → 'stalled_on_pricing'
 *     (supersedes the generic signal — one row, not two);
 *   • opened + stalled otherwise → 'viewed_no_action';
 *   • `validUntil` within {@link EXPIRING_SOON_MS} → 'expiring_soon'
 *     (can co-fire with a stall — urgency compounds).
 *
 * Pure and total: injected `now`, no clock reads, never throws.
 */
export function detectChaseSignals(input: {
  log: EngagementLog;
  /** Epoch ms the proposal expires. Use Infinity when the doc has none. */
  validUntil: number;
  now: number;
  /** Epoch ms the proposal was sent — enables the 'unopened' judgment. */
  sentAt?: number;
}): ChaseSignal[] {
  const { log, validUntil, now, sentAt } = input;

  // Past expiry is terminal — chasing a dead proposal is noise, not sales.
  if (now > validUntil) return [];

  const summary = summarizeEngagement(log, { now, sentAt });
  const closed = log.events.some((e) => e.kind === 'accepted' || e.kind === 'declined');
  if (closed) return [];

  const signals: ChaseSignal[] = [];

  if (summary.opens === 0) {
    if (typeof sentAt === 'number' && now - sentAt > UNOPENED_AFTER_MS) {
      signals.push({
        docId: log.docId,
        reason: 'unopened',
        severity: SIGNAL_SEVERITY.unopened,
      });
    }
  } else if (summary.stalled) {
    if (summary.hottestSectionId && isPricingSection(summary.hottestSectionId)) {
      signals.push({
        docId: log.docId,
        reason: 'stalled_on_pricing',
        severity: SIGNAL_SEVERITY.stalled_on_pricing,
      });
    } else {
      signals.push({
        docId: log.docId,
        reason: 'viewed_no_action',
        severity: SIGNAL_SEVERITY.viewed_no_action,
      });
    }
  }

  if (Number.isFinite(validUntil) && validUntil - now <= EXPIRING_SOON_MS) {
    signals.push({
      docId: log.docId,
      reason: 'expiring_soon',
      severity: SIGNAL_SEVERITY.expiring_soon,
    });
  }

  return signals;
}

// ---------------------------------------------------------------------------
// Drafting — real worker with deterministic fallback
// ---------------------------------------------------------------------------

/** The old→new payload every draft carries for the confirm card. */
export interface ChaseDiff {
  field: 'followup';
  before: null;
  after: string;
}

export interface ChaseDraft {
  subject: string;
  body: string;
  /** Why this draft exists — worker reasoning or the fallback's honest note. */
  reasoning: string;
  /** ActionDiff-shaped: what would be written, from nothing → this follow-up. */
  diff: ChaseDiff;
  /** The signal that triggered the draft — gateChase judges its severity. */
  signal: ChaseSignal;
}

/** The tiny drafting seam — the UI adapts argoWorker.runAgent onto this. */
export interface ChaseWorker {
  generate: (prompt: string) => Promise<{ text: string; reasoning: string }>;
}

/**
 * Deterministic per-reason follow-up copy — the offline/failure path.
 * Same signal in, same copy out, every time; no clocks, no randomness.
 */
export function fallbackChaseCopy(signal: ChaseSignal): { subject: string; body: string } {
  switch (signal.reason) {
    case 'unopened':
      return {
        subject: 'Did our proposal reach you?',
        body:
          'Just making sure our proposal landed in the right inbox — happy to ' +
          'resend it or walk through it live if that is easier. What works for you?',
      };
    case 'viewed_no_action':
      return {
        subject: 'Any questions on the proposal?',
        body:
          'Saw you had a chance to look over the proposal — is there anything ' +
          'you would like us to clarify or adjust before you decide?',
      };
    case 'stalled_on_pricing':
      return {
        subject: 'Happy to talk through the investment section',
        body:
          'It looks like the investment section is getting the closest read. ' +
          'If the numbers raise questions, a quick call usually settles them — ' +
          'want 15 minutes this week?',
      };
    case 'expiring_soon':
      return {
        subject: 'Your proposal pricing expires soon',
        body:
          'A heads-up that the pricing in your proposal is only valid a little ' +
          'longer. If you need more time or a small adjustment, tell us before ' +
          'it lapses and we will hold the terms.',
      };
  }
}

/** Deterministic reasoning line the fallback path stamps on the draft. */
function fallbackReasoning(signal: ChaseSignal): string {
  return (
    `Deterministic fallback template for '${signal.reason}' — the Argo ` +
    'agent-worker was unavailable, so no generated copy was fabricated.'
  );
}

/**
 * Build the drafting prompt from REAL engagement analytics — the worker sees
 * exactly what the buyer did, which is what makes the draft agentic rather
 * than a mail-merge token swap.
 */
export function chasePrompt(
  signal: ChaseSignal,
  ctx: { account: string; engagement: EngagementSummary },
): string {
  const e = ctx.engagement;
  return [
    `Draft a short, professional follow-up email for a proposal to ${ctx.account}.`,
    `Chase reason: ${signal.reason} (severity ${signal.severity}).`,
    `Engagement analytics: ${e.opens} open(s) from ${e.uniqueRecipients} recipient(s), ` +
      `${Math.round(e.totalDwellMs / 1000)}s total dwell, ` +
      `hottest section: ${e.hottestSectionId ?? 'none tracked'}, ` +
      `stalled: ${e.stalled ? 'yes' : 'no'}.`,
    'Speak to what the buyer actually did. Do not invent facts, names, or numbers.',
    'Return only the email body.',
  ].join('\n');
}

/**
 * Draft the follow-up for one signal.
 *
 *   • worker provided → the REAL agent-worker generates the copy from the
 *     engagement analytics (its `text` becomes the body, its `reasoning`
 *     rides along verbatim);
 *   • worker absent OR its generate() rejects/returns junk → the
 *     DETERMINISTIC {@link fallbackChaseCopy} template, byte-identical to
 *     calling it directly — the offline path is a first-class citizen, not
 *     a degraded one.
 *
 * Every draft carries the ActionDiff-shaped `{field:'followup', before:null,
 * after}` payload so the approval card can show exactly what would be sent.
 */
export async function draftChase(
  signal: ChaseSignal,
  ctx: { account: string; engagement: EngagementSummary },
  worker?: ChaseWorker,
): Promise<ChaseDraft> {
  const fallback = fallbackChaseCopy(signal);

  let subject = fallback.subject;
  let body = fallback.body;
  let reasoning = fallbackReasoning(signal);

  if (worker) {
    try {
      const out = await worker.generate(chasePrompt(signal, ctx));
      if (typeof out?.text === 'string' && out.text.trim() !== '') {
        body = out.text;
        reasoning =
          typeof out.reasoning === 'string' && out.reasoning.trim() !== ''
            ? out.reasoning
            : 'Drafted by the Argo agent-worker from live engagement analytics.';
        // A worker may lead with an explicit "Subject:" line — honor it.
        const m = /^subject:\s*(.+)$/im.exec(out.text);
        if (m) {
          subject = m[1].trim();
          body = out.text.replace(m[0], '').trim();
        }
      }
    } catch {
      // Worker unreachable/broken → the deterministic fallback stands.
      subject = fallback.subject;
      body = fallback.body;
      reasoning = fallbackReasoning(signal);
    }
  }

  return {
    subject,
    body,
    reasoning,
    diff: { field: 'followup', before: null, after: `${subject}\n\n${body}` },
    signal,
  };
}

// ---------------------------------------------------------------------------
// Gate — approval-gated sends per earned autonomy tier
// ---------------------------------------------------------------------------

/**
 * Decide whether a drafted chase may auto-send or must go to the approvals
 * inbox:
 *
 *   • 'unopened' NEVER auto-sends — cold outreach to a buyer with zero
 *     recorded engagement is a policy/margin risk regardless of tier;
 *   • severity above the earned `tier` → 'require_approval';
 *   • severity above `maxAutoSeverity` → 'require_approval';
 *   • otherwise → 'auto_send'.
 *
 * The require_approval path pairs with {@link chaseApprovalAction} to
 * produce the approvalQueue-shaped item queueApproval expects.
 */
export function gateChase(
  draft: ChaseDraft,
  autonomy: { tier: number; maxAutoSeverity: number },
): { action: 'auto_send' | 'require_approval'; reason: string } {
  const sig = draft.signal;
  if (sig.reason === 'unopened') {
    return {
      action: 'require_approval',
      reason:
        'Cold follow-up on an unopened proposal never auto-sends — a human ' +
        'must own outreach to a buyer with zero recorded engagement.',
    };
  }
  if (sig.severity > autonomy.tier) {
    return {
      action: 'require_approval',
      reason:
        `Signal severity ${sig.severity} is above the earned autonomy tier ` +
        `${autonomy.tier} — routed to the approvals inbox.`,
    };
  }
  if (sig.severity > autonomy.maxAutoSeverity) {
    return {
      action: 'require_approval',
      reason:
        `Signal severity ${sig.severity} exceeds the auto-send ceiling ` +
        `${autonomy.maxAutoSeverity} — routed to the approvals inbox.`,
    };
  }
  return {
    action: 'auto_send',
    reason:
      `Severity ${sig.severity} is within the earned tier ${autonomy.tier} ` +
      `and the auto-send ceiling ${autonomy.maxAutoSeverity}.`,
  };
}

/**
 * Wrap a draft as the AgentAction the shared approvals pipeline consumes
 * (queueApproval → operator sign-off → executor). A high-priority task —
 * never a direct send: the operator sends the email; the agent drafted it.
 */
export function chaseApprovalAction(
  draft: ChaseDraft,
  ctx: { account: string; accountId?: string },
): AgentAction {
  return {
    kind: 'create_task',
    title: `Chase proposal — ${draft.subject}`,
    priority: 'high',
    accountId: ctx.accountId,
    accountName: ctx.account,
  };
}

// ---------------------------------------------------------------------------
// UI bridge — derive an HONEST log from what the doc actually recorded
// ---------------------------------------------------------------------------

/**
 * Build an engagement log from a persisted document's own status record.
 * Only statuses the doc genuinely reached become events — 'viewed' proves
 * at least one open, 'accepted'/'signed' prove an open + an acceptance,
 * 'declined' proves an open + a decline. Draft/sent docs yield an EMPTY log:
 * we never fabricate buyer behavior that was not recorded.
 */
export function logFromDocSnapshot(doc: {
  id: string;
  status: string;
  entityId: string;
  updatedAt: string;
  acceptedAt?: string;
}): EngagementLog {
  const events: EngagementEvent[] = [];
  const token = `doc:${doc.entityId}`;
  const updatedMs = Date.parse(doc.updatedAt);
  const ts = Number.isFinite(updatedMs) ? updatedMs : 0;

  if (doc.status === 'viewed') {
    events.push({ kind: 'opened', recipientToken: token, ts });
  } else if (doc.status === 'accepted' || doc.status === 'signed') {
    const acceptedMs = doc.acceptedAt ? Date.parse(doc.acceptedAt) : ts;
    const at = Number.isFinite(acceptedMs) ? acceptedMs : ts;
    events.push({ kind: 'opened', recipientToken: token, ts: at });
    events.push({ kind: 'accepted', recipientToken: token, ts: at });
  } else if (doc.status === 'declined') {
    events.push({ kind: 'opened', recipientToken: token, ts });
    events.push({ kind: 'declined', recipientToken: token, ts });
  }

  return { docId: doc.id, events };
}
